import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Well } from "@zendeskgarden/react-notifications";
import { Tag } from "@zendeskgarden/react-tags";
import { useNavigate, useLocation } from "react-router-dom";
import { casesApi } from "../../api";
import { useAuth } from "../../utils/auth";
import ImagePredictor from "../predictor/predictor";
import Cases from "../cases/cases";
import FilterableTable, { Column } from "../common/FilterableTable";
import DashboardLayout from "../common/DashboardLayout";
import Profile from "../common/Profile";
import HelpSupport from "../common/HelpSupport";
import { CasesIcon, ProfileIcon, HelpSupportIcon } from "../common/Icons";

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 24px;
  margin-bottom: 32px;
`;

const StatCard = styled(Well)`
  padding: 24px;
  text-align: center;
  border-radius: 8px;
`;

const StatNumber = styled.div`
  font-size: 32px;
  font-weight: bold;
  color: #8D4F1B;
  margin-bottom: 8px;
  font-family: 'SF Pro Display', 'SF Pro Text', -apple-system, BlinkMacSystemFont, sans-serif;
`;

const StatLabel = styled.div`
  color: #68737d;
  font-size: 14px;
  font-family: 'SF Pro Display', 'SF Pro Text', -apple-system, BlinkMacSystemFont, sans-serif;
`;

interface Case {
  id: string;
  doctorDiagnosis: string;
  doctorNote: string;
  reviewed: boolean;
  reviewedDiagnosis: string;
  reviewerNote: string;
}

const PhysicianDashboard: React.FC = () => {
  const { user } = useAuth();
  const [cases, setCases] = useState<Case[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'cases' | 'profile' | 'help'>('cases');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    async function fetchCases() {
      setLoading(true);
      try {
        const res = await casesApi.getMyCases();
        const transformedCases = (res.data.cases || res.data).map((caseData: any) => ({
          id: caseData._id,
          doctorDiagnosis: caseData.doctorDiagnosis,
          doctorNote: caseData.doctorNote,
          reviewed: caseData.reviewStatus === 'approved' || caseData.reviewStatus === 'rejected',
          reviewedDiagnosis: caseData.reviewNote || '',
          reviewerNote: caseData.reviewNote || ''
        }));
        setCases(transformedCases);
      } catch (e) {
        console.error("Failed to fetch cases:", e);
      }
      setLoading(false);
    }
    fetchCases();
  }, []);

  // Determine active tab from URL
  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/profile')) {
      setActiveTab('profile');
    } else if (path.includes('/help')) {
      setActiveTab('help');
    } else {
      setActiveTab('cases');
    }
  }, [location.pathname]);

  const stats = {
    totalCases: cases.length,
    reviewedCases: cases.filter(c => c.reviewed).length,
    pendingCases: cases.filter(c => !c.reviewed).length,
  };

  const handleNavigation = (tabId: string) => {
    const tab = tabId as 'cases' | 'profile' | 'help';
    setActiveTab(tab);
    switch (tab) {
      case 'profile':
        navigate('/physician/profile');
        break;
      case 'help':
        navigate('/physician/help');
        break;
      default:
        navigate('/physician/cases');
        break;
    }
  };

  const getPageTitle = () => {
    switch (activeTab) {
      case 'profile':
        return 'Profile';
      case 'help':
        return 'Help & Support';
      default:
        return 'My Cases';
    }
  };

  const getPageDescription = () => {
    switch (activeTab) {
      case 'profile':
        return 'Manage your profile settings and preferences.';
      case 'help':
        return 'Get help and support for using the platform.';
      default:
        return 'Manage and review your uploaded cases and diagnoses.';
    }
  };

  const menuItems = [
    {
      id: 'cases',
      label: 'My Cases',
      icon: <CasesIcon />,
      path: '/physician/cases'
    },
    {
      id: 'profile',
      label: 'Profile',
      icon: <ProfileIcon />,
      path: '/physician/profile'
    },
    {
      id: 'help',
      label: 'Help & Support',
      icon: <HelpSupportIcon />,
      path: '/physician/help'
    }
  ];

  // Define table columns
  const caseColumns: Column[] = [
    { key: 'id', title: 'Case ID' },
    { key: 'doctorDiagnosis', title: 'My Diagnosis' },
    { key: 'doctorNote', title: 'My Note' },
    { 
      key: 'reviewed', 
      title: 'Status',
      render: (value) => (
        <Tag hue={value ? 'green' : 'orange'} size="small">
          {value ? 'Reviewed' : 'Pending'}
        </Tag>
      )
    },
    { key: 'reviewedDiagnosis', title: 'Reviewer Feedback' }
  ];

  const renderMainContent = () => {
    switch (activeTab) {
      case 'cases':
        return <Cases />;
      case 'profile':
        return <Profile />;
      case 'help':
        return <HelpSupport />;
      default:
        return <Cases />;
    }
  };

  return (
    <DashboardLayout
      title={getPageTitle()}
      userInfo={`${user?.name || 'Dr. User'} • ${user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : 'Physician'}`}
      activeTab={activeTab}
      menuItems={menuItems}
      onTabChange={handleNavigation}
      breadcrumb={getPageTitle()}
      userName={user?.name || 'Dr. User'}
      userType={user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : 'Physician'}
      pageDescription={getPageDescription()}
    >
      {renderMainContent()}
    </DashboardLayout>
  );
};

export default PhysicianDashboard;
